/* eslint-disable no-unused-vars */
// =============================================================================
// MarketingPromotion.js — FULL REBUILD
// JobEng — Business / Marketing hub
// Location: app/screens/business/marketing/MarketingPromotion.js
//
// Tabs: Dashboard · Campaigns · Content · Analytics · Insights
// Live DB: /api/data/campaigns, /api/data/design_assets, /api/data/content_ideas
//          (content_ideas falls back to AsyncStorage if table absent)
// AI:      /api/ai (Groq proxy) — content ideas + portfolio insights
// DB connection monitor: /health ping with status badge + retry
// =============================================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Dimensions,
  TextInput,
  Image,
  ScrollView,
  Modal,
  Alert,
  ActivityIndicator,
  RefreshControl,
  Platform,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';

const { width: SCREEN_W } = Dimensions.get('window');

const API_BASE = 'https://api.jobeng.ai';
const API_KEY =
  '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const getH = async () => {
  const tok =
    (await AsyncStorage.getItem('auth_token').catch(() => null)) ||
    (await AsyncStorage.getItem('userToken').catch(() => null));
  return {
    'Content-Type': 'application/json',
    'x-api-key': API_KEY,
    ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
  };
};

const getBid = async () =>
  parseInt(await AsyncStorage.getItem('company_id').catch(() => '0'), 10) || 0;

const C = {
  primary: '#0e4e96',
  primaryDark: '#093a72',
  primaryLight: '#1e7be2',
  accent: '#EC4899',
  bg: '#F4F6FB',
  card: '#FFFFFF',
  text: '#0F172A',
  sub: '#64748B',
  border: '#E2E8F0',
  success: '#10B981',
  warning: '#F59E0B',
  danger: '#EF4444',
  purple: '#7C3AED',
  teal: '#0891B2',
};

const TABS = [
  { key: 'Dashboard', icon: 'view-dashboard-outline' },
  { key: 'Campaigns', icon: 'bullhorn-outline' },
  { key: 'Content', icon: 'palette-outline' },
  { key: 'Analytics', icon: 'chart-bar' },
  { key: 'Insights', icon: 'lightbulb-on-outline' },
];

const STATUS_META = {
  draft: { label: 'Draft', color: '#94A3B8' },
  active: { label: 'Active', color: C.success },
  paused: { label: 'Paused', color: C.warning },
  completed: { label: 'Completed', color: '#6366F1' },
};

const CHANNEL_META = {
  email: { label: 'Email', icon: 'email-outline', color: C.teal },
  social: { label: 'Social', icon: 'share-variant-outline', color: C.accent },
  job_board: { label: 'Job Boards', icon: 'briefcase-outline', color: C.primary },
  sms: { label: 'SMS', icon: 'message-text-outline', color: C.success },
  ppc: { label: 'Paid Ads', icon: 'cursor-default-click-outline', color: C.warning },
  content: { label: 'Content', icon: 'post-outline', color: C.purple },
};

const STUDIO_TOOLS = [
  { key: 'blog', label: 'Blog Post', icon: 'post-outline', color: C.teal, nav: 'BlogEditor' },
  { key: 'image', label: 'Image Creator', icon: 'image-edit-outline', color: C.purple, nav: 'ImageCreator' },
  { key: 'video', label: 'Video Editor', icon: 'movie-open-edit-outline', color: C.accent, nav: 'VideoEditor' },
  { key: 'email', label: 'Email Templates', icon: 'email-edit-outline', color: C.primaryLight, nav: 'CampaignEmailTemplatesScreen' },
  { key: 'ai', label: 'AI Assistant', icon: 'brain', color: C.danger, nav: 'CampaignAIContentAssistantScreen' },
];

const IDEA_TYPES = ['Blog Post', 'Video', 'Social Media', 'Email', 'Infographic'];

const toNum = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const money = (n) => `£${toNum(n).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;
const compact = (n) => {
  const v = toNum(n);
  if (v >= 1000000) return `${(v / 1000000).toFixed(1)}M`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return String(v);
};

const parseChannels = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try { const j = JSON.parse(raw); if (Array.isArray(j)) return j; } catch {}
    return raw.split(',').map((s) => s.trim()).filter(Boolean);
  }
  return [];
};

const normCampaign = (c) => ({
  id: c.id,
  name: c.name || c.campaign_name || 'Untitled',
  status: (c.status || 'draft').toLowerCase(),
  objective: c.objective || '',
  channels: parseChannels(c.channels || c.channel),
  budget: toNum(c.budget),
  spent: toNum(c.spent || c.spend),
  impressions: toNum(c.impressions || c.reach || c.total_reach),
  clicks: toNum(c.clicks),
  conversions: toNum(c.conversions),
  start_date: c.start_date ? String(c.start_date).slice(0, 10) : '',
  end_date: c.end_date ? String(c.end_date).slice(0, 10) : '',
  created_at: c.created_at || null,
  _raw: c,
});

// =============================================================================
// MODULE-SCOPE COMPONENTS (React.memo)
// =============================================================================

const CompanyLogo = React.memo(({ uri, size = 30 }) => {
  if (uri && typeof uri === 'string' && (uri.startsWith('http') || uri.startsWith('data:'))) {
    return (
      <Image source={{ uri }} style={{ width: size, height: size, borderRadius: 7 }} resizeMode="contain" onError={() => {}} />
    );
  }
  return (
    <View style={{ width: size, height: size, borderRadius: 7, backgroundColor: 'rgba(255,255,255,0.2)', alignItems: 'center', justifyContent: 'center' }}>
      <MaterialCommunityIcons name="office-building" size={size * 0.6} color="#fff" />
    </View>
  );
});

const DbBadge = React.memo(({ status, onPress }) => {
  const cfg =
    status === 'online' ? { c: '#34D399', t: 'Live' }
    : status === 'offline' ? { c: '#F87171', t: 'Offline' }
    : { c: '#FBBF24', t: '…' };
  return (
    <TouchableOpacity style={s.dbBadge} onPress={onPress} activeOpacity={0.7}>
      <View style={[s.dbDot, { backgroundColor: cfg.c }]} />
      <Text style={s.dbText}>{cfg.t}</Text>
    </TouchableOpacity>
  );
});

const StatTile = React.memo(({ icon, label, value, color, sub }) => (
  <View style={s.statTile}>
    <View style={[s.statIcon, { backgroundColor: `${color}15` }]}>
      <MaterialCommunityIcons name={icon} size={18} color={color} />
    </View>
    <Text style={s.statValue} numberOfLines={1}>{value}</Text>
    <Text style={s.statLabel} numberOfLines={1}>{label}</Text>
    {sub ? <Text style={[s.statSub, { color }]} numberOfLines={1}>{sub}</Text> : null}
  </View>
));

const QuickAction = React.memo(({ icon, label, color, onPress }) => (
  <TouchableOpacity style={s.quickAction} onPress={onPress} activeOpacity={0.7}>
    <View style={[s.quickIcon, { backgroundColor: `${color}15` }]}>
      <MaterialCommunityIcons name={icon} size={22} color={color} />
    </View>
    <Text style={s.quickLabel} numberOfLines={2}>{label}</Text>
  </TouchableOpacity>
));

const Chip = React.memo(({ label, active, onPress, count }) => (
  <TouchableOpacity style={[s.chip, active && s.chipActive]} onPress={onPress} activeOpacity={0.7}>
    <Text style={[s.chipText, active && s.chipTextActive]}>
      {label}{count !== undefined ? ` (${count})` : ''}
    </Text>
  </TouchableOpacity>
));

const SectionHeader = React.memo(({ title, actionLabel, onAction }) => (
  <View style={s.sectionHeader}>
    <Text style={s.sectionTitle}>{title}</Text>
    {actionLabel ? (
      <TouchableOpacity onPress={onAction}>
        <Text style={s.sectionAction}>{actionLabel}</Text>
      </TouchableOpacity>
    ) : null}
  </View>
));

const CampaignCard = React.memo(({ c, onOpen, onAnalytics, onToggle, onVideo }) => {
  const meta = STATUS_META[c.status] || STATUS_META.draft;
  const pct = c.budget > 0 ? Math.min(100, (c.spent / c.budget) * 100) : 0;
  const over = c.budget > 0 && c.spent > c.budget;
  const ctr = c.impressions > 0 ? ((c.clicks / c.impressions) * 100).toFixed(1) : '0.0';
  return (
    <View style={s.campCard}>
      <TouchableOpacity onPress={() => onOpen(c)} activeOpacity={0.7}>
        <View style={s.campHead}>
          <View style={{ flex: 1, marginRight: 8 }}>
            <Text style={s.campName} numberOfLines={1}>{c.name}</Text>
            {!!c.objective && <Text style={s.campObjective} numberOfLines={1}>{c.objective}</Text>}
          </View>
          <View style={[s.statusPill, { backgroundColor: `${meta.color}18` }]}>
            <Text style={[s.statusPillText, { color: meta.color }]}>{meta.label}</Text>
          </View>
        </View>

        {c.channels.length > 0 && (
          <View style={s.channelRow}>
            {c.channels.slice(0, 4).map((ch) => {
              const cm = CHANNEL_META[ch];
              return cm ? (
                <View key={ch} style={[s.channelBadge, { backgroundColor: `${cm.color}12` }]}>
                  <MaterialCommunityIcons name={cm.icon} size={11} color={cm.color} />
                  <Text style={[s.channelText, { color: cm.color }]}>{cm.label}</Text>
                </View>
              ) : null;
            })}
          </View>
        )}

        <View style={s.budgetRow}>
          <Text style={s.budgetText}>
            {money(c.spent)} <Text style={{ color: C.sub, fontWeight: '500' }}>/ {money(c.budget)}</Text>
          </Text>
          <Text style={[s.budgetPct, { color: over ? C.danger : C.sub }]}>{pct.toFixed(0)}%</Text>
        </View>
        <View style={s.track}>
          <View style={[s.fill, { width: `${pct}%`, backgroundColor: over ? C.danger : C.primaryLight }]} />
        </View>

        <View style={s.metricsRow}>
          <View style={s.metric}><Text style={s.metricV}>{compact(c.impressions)}</Text><Text style={s.metricL}>Reach</Text></View>
          <View style={s.metric}><Text style={s.metricV}>{compact(c.clicks)}</Text><Text style={s.metricL}>Clicks</Text></View>
          <View style={s.metric}><Text style={s.metricV}>{ctr}%</Text><Text style={s.metricL}>CTR</Text></View>
          <View style={s.metric}><Text style={s.metricV}>{compact(c.conversions)}</Text><Text style={s.metricL}>Conv.</Text></View>
        </View>
      </TouchableOpacity>

      <View style={s.campActions}>
        {c.status !== 'completed' && (
          <TouchableOpacity style={s.campBtn} onPress={() => onToggle(c)}>
            <Ionicons name={c.status === 'active' ? 'pause' : 'play'} size={14} color={C.primary} />
            <Text style={s.campBtnText}>{c.status === 'active' ? 'Pause' : 'Activate'}</Text>
          </TouchableOpacity>
        )}
        <TouchableOpacity style={s.campBtn} onPress={() => onAnalytics(c)}>
          <MaterialCommunityIcons name="chart-line" size={14} color={C.purple} />
          <Text style={[s.campBtnText, { color: C.purple }]}>Analytics</Text>
        </TouchableOpacity>
        <TouchableOpacity style={s.campBtn} onPress={() => onVideo(c)}>
          <MaterialCommunityIcons name="movie-open-outline" size={14} color={C.accent} />
          <Text style={[s.campBtnText, { color: C.accent }]}>Video</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
});

const IdeaCard = React.memo(({ idea, onToggle, onDelete }) => (
  <View style={s.ideaCard}>
    <TouchableOpacity style={s.ideaCheck} onPress={() => onToggle(idea.id)}>
      <Ionicons
        name={idea.done ? 'checkmark-circle' : 'ellipse-outline'}
        size={22}
        color={idea.done ? C.success : C.sub}
      />
    </TouchableOpacity>
    <View style={{ flex: 1 }}>
      <Text style={[s.ideaTitle, idea.done && { textDecorationLine: 'line-through', color: C.sub }]}>
        {idea.title}
      </Text>
      <View style={s.ideaMetaRow}>
        <View style={s.ideaType}><Text style={s.ideaTypeText}>{idea.type}</Text></View>
        {!!idea.source && <Text style={s.ideaSource}>{idea.source === 'ai' ? '✨ AI' : 'Manual'}</Text>}
      </View>
    </View>
    <TouchableOpacity style={{ padding: 6 }} onPress={() => onDelete(idea.id)}>
      <Ionicons name="trash-outline" size={16} color={C.danger} />
    </TouchableOpacity>
  </View>
));

const BarRow = React.memo(({ label, value, max, color, display }) => (
  <View style={s.barRow}>
    <Text style={s.barLabel} numberOfLines={1}>{label}</Text>
    <View style={s.barTrack}>
      <View style={[s.barFill, { width: `${max > 0 ? Math.max(2, (value / max) * 100) : 2}%`, backgroundColor: color }]} />
    </View>
    <Text style={s.barValue}>{display}</Text>
  </View>
));

// =============================================================================
// MAIN SCREEN
// =============================================================================

const MarketingPromotionScreen = ({ navigation }) => {
  const [companyLogo, setCompanyLogo] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [businessId, setBusinessId] = useState(0);
  const [activeTab, setActiveTab] = useState('Dashboard');

  const [dbStatus, setDbStatus] = useState('checking'); // checking | online | offline
  const [dbDetail, setDbDetail] = useState('');

  const [campaigns, setCampaigns] = useState([]);
  const [assetsCount, setAssetsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [ideas, setIdeas] = useState([]);
  const [ideaModal, setIdeaModal] = useState(false);
  const [ideaTitle, setIdeaTitle] = useState('');
  const [ideaType, setIdeaType] = useState('Blog Post');
  const [aiBusy, setAiBusy] = useState(false);

  const [insights, setInsights] = useState('');
  const [insightsBusy, setInsightsBusy] = useState(false);

  // ---------------------------------------------------------------------------
  // DB CONNECTION CHECK
  // ---------------------------------------------------------------------------

  const checkDb = useCallback(async () => {
    setDbStatus('checking');
    try {
      const h = await getH();
      const t0 = Date.now();
      const health = await fetch(`${API_BASE}/health`, { headers: h })
        .then((r) => r.ok)
        .catch(() => false);
      const data = await fetch(`${API_BASE}/api/data/campaigns?limit=1`, { headers: h })
        .then((r) => r.ok)
        .catch(() => false);
      const ms = Date.now() - t0;
      if (health && data) {
        setDbStatus('online');
        setDbDetail(`API + database reachable · ${ms}ms`);
      } else if (health) {
        setDbStatus('offline');
        setDbDetail('API up but campaigns table unreachable — check /api/data/campaigns');
      } else {
        setDbStatus('offline');
        setDbDetail('API not responding — check PM2 / nginx on the server');
      }
    } catch (e) {
      setDbStatus('offline');
      setDbDetail(e?.message || 'Network error');
    }
  }, []);

  const showDbDetail = useCallback(() => {
    Alert.alert(
      dbStatus === 'online' ? '✅ Connected' : '⚠️ Connection issue',
      dbDetail || 'Checking…',
      [{ text: 'Re-check', onPress: checkDb }, { text: 'OK' }]
    );
  }, [dbStatus, dbDetail, checkDb]);

  // ---------------------------------------------------------------------------
  // LOAD
  // ---------------------------------------------------------------------------

  const loadAll = useCallback(async (spinner = true) => {
    if (spinner) setLoading(true);
    const bid = await getBid();
    setBusinessId(bid);
    if (!bid) { setCampaigns([]); setLoading(false); setRefreshing(false); return; }
    const h = await getH();

    // Campaigns
    try {
      const res = await fetch(`${API_BASE}/api/data/campaigns?company_id=${bid}&limit=100`, { headers: h })
        .then((r) => r.json()).catch(() => null);
      const rows = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
      const list = rows.map(normCampaign).sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')));
      setCampaigns(list);
      AsyncStorage.setItem('campaigns_cache', JSON.stringify(rows.slice(0, 50))).catch(() => {});
    } catch {
      const cached = await AsyncStorage.getItem('campaigns_cache').catch(() => null);
      if (cached) { try { setCampaigns(JSON.parse(cached).map(normCampaign)); } catch {} }
    }

    // Content assets count (video projects etc.)
    try {
      const res = await fetch(`${API_BASE}/api/data/design_assets?company_id=${bid}&limit=100`, { headers: h })
        .then((r) => r.json()).catch(() => null);
      const rows = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
      setAssetsCount(rows.length);
    } catch {}

    // Content ideas: try table, fall back to local
    let ideasLoaded = false;
    try {
      const res = await fetch(`${API_BASE}/api/data/content_ideas?company_id=${bid}&limit=100`, { headers: h })
        .then((r) => r.json()).catch(() => null);
      if (Array.isArray(res?.data)) {
        setIdeas(res.data.map((i) => ({
          id: i.id, title: i.title, type: i.type || 'Blog Post',
          done: !!i.done || i.status === 'done', source: i.source || 'manual', remote: true,
        })));
        ideasLoaded = true;
      }
    } catch {}
    if (!ideasLoaded) {
      const raw = await AsyncStorage.getItem(`content_ideas_${bid}`).catch(() => null);
      if (raw) { try { setIdeas(JSON.parse(raw)); } catch {} }
    }

    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const raw = await AsyncStorage.getItem('company_profile');
        if (raw) {
          const p = JSON.parse(raw);
          const logo = p.logo || p.logo_url || p.company_logo;
          if (logo && typeof logo === 'string') {
            if (logo.startsWith('http') || logo.startsWith('data:')) setCompanyLogo(logo);
            else if (logo.startsWith('/')) setCompanyLogo(API_BASE + logo);
          }
          setCompanyName(p.name || p.company_name || '');
        }
      } catch {}
      checkDb();
      loadAll();
    })();
    const unsub = navigation?.addListener?.('focus', () => loadAll(false));
    return unsub;
  }, [checkDb, loadAll, navigation]);

  const persistIdeas = useCallback(async (list) => {
    const bid = await getBid();
    AsyncStorage.setItem(`content_ideas_${bid}`, JSON.stringify(list.slice(0, 100))).catch(() => {});
  }, []);

  // ---------------------------------------------------------------------------
  // CAMPAIGN ACTIONS
  // ---------------------------------------------------------------------------

  const toggleCampaign = useCallback(async (c) => {
    const next = c.status === 'active' ? 'paused' : 'active';
    setCampaigns((prev) => prev.map((x) => (x.id === c.id ? { ...x, status: next } : x)));
    try {
      const h = await getH();
      const res = await fetch(`${API_BASE}/api/data/campaigns/${c.id}`, {
        method: 'PUT', headers: h, body: JSON.stringify({ status: next }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    } catch (e) {
      setCampaigns((prev) => prev.map((x) => (x.id === c.id ? { ...x, status: c.status } : x)));
      Alert.alert('Update failed', e?.message || 'Could not update campaign status.');
    }
  }, []);

  // ---------------------------------------------------------------------------
  // CONTENT IDEAS
  // ---------------------------------------------------------------------------

  const addIdea = useCallback(async () => {
    if (!ideaTitle.trim()) { Alert.alert('Title needed', 'Enter the content idea.'); return; }
    const idea = { id: `idea_${Date.now()}`, title: ideaTitle.trim(), type: ideaType, done: false, source: 'manual' };
    const next = [idea, ...ideas];
    setIdeas(next);
    persistIdeas(next);
    setIdeaModal(false);
    setIdeaTitle('');
    // best-effort remote save
    try {
      const h = await getH();
      const bid = await getBid();
      fetch(`${API_BASE}/api/data/content_ideas`, {
        method: 'POST', headers: h,
        body: JSON.stringify({ company_id: bid, title: idea.title, type: idea.type, source: 'manual', done: false }),
      }).catch(() => {});
    } catch {}
  }, [ideaTitle, ideaType, ideas, persistIdeas]);

  const toggleIdea = useCallback((id) => {
    setIdeas((prev) => {
      const next = prev.map((i) => (i.id === id ? { ...i, done: !i.done } : i));
      persistIdeas(next);
      return next;
    });
  }, [persistIdeas]);

  const deleteIdea = useCallback((id) => {
    setIdeas((prev) => {
      const next = prev.filter((i) => i.id !== id);
      persistIdeas(next);
      return next;
    });
  }, [persistIdeas]);

  const aiGenerateIdeas = useCallback(async () => {
    setAiBusy(true);
    try {
      const h = await getH();
      const campCtx = campaigns.slice(0, 5).map((c) => `${c.name} (${c.objective || 'no objective'})`).join('; ');
      const prompt =
        `Generate 6 specific content ideas for ${companyName || 'a UK business'} on the JobEng platform.` +
        (campCtx ? ` Active campaigns: ${campCtx}.` : '') +
        ` Mix of blog posts, social media, video, and email. Respond ONLY with a JSON array, no markdown, no preamble: [{"title":"...","type":"Blog Post|Video|Social Media|Email|Infographic"}]`;
      const res = await fetch(`${API_BASE}/api/ai`, {
        method: 'POST', headers: h, body: JSON.stringify({ prompt }),
      }).then((r) => r.json()).catch(() => null);
      const content = res?.content || '';
      let arr = [];
      try { arr = JSON.parse(content.replace(/```json|```/g, '').trim()); } catch {}
      if (!Array.isArray(arr) || !arr.length) throw new Error('AI returned no usable ideas');
      const newIdeas = arr.slice(0, 8).map((i, idx) => ({
        id: `ai_${Date.now()}_${idx}`,
        title: String(i.title || '').slice(0, 140),
        type: IDEA_TYPES.includes(i.type) ? i.type : 'Blog Post',
        done: false,
        source: 'ai',
      })).filter((i) => i.title);
      const next = [...newIdeas, ...ideas];
      setIdeas(next);
      persistIdeas(next);
    } catch (e) {
      Alert.alert('AI unavailable', e?.message || 'Could not generate ideas.');
    } finally {
      setAiBusy(false);
    }
  }, [campaigns, companyName, ideas, persistIdeas]);

  // ---------------------------------------------------------------------------
  // AI INSIGHTS
  // ---------------------------------------------------------------------------

  const generateInsights = useCallback(async () => {
    if (!campaigns.length) { Alert.alert('No data', 'Create campaigns first — insights are based on real performance.'); return; }
    setInsightsBusy(true);
    try {
      const h = await getH();
      const summary = campaigns.slice(0, 15).map((c) =>
        `- ${c.name} [${c.status}] budget £${c.budget}, spent £${c.spent}, reach ${c.impressions}, clicks ${c.clicks}, conv ${c.conversions}, channels: ${c.channels.join('/') || 'none'}`
      ).join('\n');
      const prompt =
        `You are a marketing analyst for a UK SME. Analyse this campaign portfolio and write: (1) a 2-sentence performance summary, (2) the single biggest problem, (3) 4 prioritised recommendations with expected impact. Plain text, British English, no markdown symbols:\n${summary}`;
      const res = await fetch(`${API_BASE}/api/ai`, {
        method: 'POST', headers: h, body: JSON.stringify({ prompt }),
      }).then((r) => r.json()).catch(() => null);
      if (!res?.content) throw new Error(res?.message || 'No response');
      setInsights(String(res.content).trim());
    } catch (e) {
      Alert.alert('AI unavailable', e?.message || 'Could not generate insights.');
    } finally {
      setInsightsBusy(false);
    }
  }, [campaigns]);

  // ---------------------------------------------------------------------------
  // DERIVED
  // ---------------------------------------------------------------------------

  const stats = useMemo(() => {
    const active = campaigns.filter((c) => c.status === 'active').length;
    const budget = campaigns.reduce((s2, c) => s2 + c.budget, 0);
    const spent = campaigns.reduce((s2, c) => s2 + c.spent, 0);
    const reach = campaigns.reduce((s2, c) => s2 + c.impressions, 0);
    const clicks = campaigns.reduce((s2, c) => s2 + c.clicks, 0);
    const conv = campaigns.reduce((s2, c) => s2 + c.conversions, 0);
    const ctr = reach > 0 ? ((clicks / reach) * 100).toFixed(1) : '0.0';
    const convRate = clicks > 0 ? ((conv / clicks) * 100).toFixed(1) : '0.0';
    return { active, budget, spent, reach, clicks, conv, ctr, convRate };
  }, [campaigns]);

  const channelStats = useMemo(() => {
    const map = {};
    campaigns.forEach((c) => {
      c.channels.forEach((ch) => {
        map[ch] = map[ch] || { reach: 0, clicks: 0, count: 0 };
        // attribute evenly across the campaign's channels
        const share = c.channels.length || 1;
        map[ch].reach += c.impressions / share;
        map[ch].clicks += c.clicks / share;
        map[ch].count += 1;
      });
    });
    return Object.entries(map)
      .map(([key, v]) => ({ key, ...v, meta: CHANNEL_META[key] }))
      .filter((x) => x.meta)
      .sort((a, b) => b.reach - a.reach);
  }, [campaigns]);

  const topCampaigns = useMemo(
    () => [...campaigns].sort((a, b) => b.impressions - a.impressions).slice(0, 5),
    [campaigns]
  );

  const filteredCampaigns = useMemo(() => {
    const q = search.trim().toLowerCase();
    return campaigns.filter((c) => {
      if (statusFilter !== 'all' && c.status !== statusFilter) return false;
      if (!q) return true;
      return c.name.toLowerCase().includes(q) || c.objective.toLowerCase().includes(q);
    });
  }, [campaigns, search, statusFilter]);

  const statusCounts = useMemo(() => {
    const m = { all: campaigns.length };
    Object.keys(STATUS_META).forEach((k) => { m[k] = campaigns.filter((c) => c.status === k).length; });
    return m;
  }, [campaigns]);

  const refresh = useCallback(() => { setRefreshing(true); checkDb(); loadAll(false); }, [checkDb, loadAll]);

  const goVideo = useCallback((c) => navigation.navigate('VideoEditor', { campaignId: c?.id, campaign: c?._raw || c }), [navigation]);

  // ---------------------------------------------------------------------------
  // TAB RENDERERS
  // ---------------------------------------------------------------------------

  const renderDashboard = () => (
    <ScrollView style={s.tabContent} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={refresh} tintColor={C.primary} />}>
      <View style={s.heroCard}>
        <View style={{ flex: 1 }}>
          <Text style={s.heroTitle}>Marketing Hub</Text>
          <Text style={s.heroSub}>{companyName || 'Your business'} · {campaigns.length} campaigns · {assetsCount} content assets</Text>
        </View>
        <MaterialCommunityIcons name="bullhorn" size={40} color="rgba(255,255,255,0.35)" />
      </View>

      <View style={s.statGrid}>
        <StatTile icon="bullhorn-outline" label="Active campaigns" value={String(stats.active)} color={C.success} />
        <StatTile icon="cash-multiple" label="Total budget" value={money(stats.budget)} color={C.primaryLight} sub={`${money(stats.spent)} spent`} />
        <StatTile icon="eye-outline" label="Total reach" value={compact(stats.reach)} color={C.purple} />
        <StatTile icon="chart-line" label="Avg CTR" value={`${stats.ctr}%`} color={C.accent} sub={`${stats.convRate}% conv.`} />
      </View>

      <SectionHeader title="Quick Actions" />
      <View style={s.quickGrid}>
        <QuickAction icon="plus-circle-outline" label="New Campaign" color={C.success} onPress={() => navigation.navigate('CreateCampaign')} />
        <QuickAction icon="brain" label="AI Assistant" color={C.danger} onPress={() => navigation.navigate('CampaignAIContentAssistantScreen', { campaign: campaigns[0]?._raw })} />
        <QuickAction icon="post-outline" label="Blog Post" color={C.teal} onPress={() => navigation.navigate('BlogEditor', { campaignId: campaigns[0]?.id })} />
        <QuickAction icon="image-edit-outline" label="Image" color={C.purple} onPress={() => navigation.navigate('ImageCreator', { campaignId: campaigns[0]?.id })} />
        <QuickAction icon="movie-open-edit-outline" label="Video" color={C.accent} onPress={() => goVideo(campaigns[0])} />
        <QuickAction icon="email-edit-outline" label="Email" color={C.primaryLight} onPress={() => navigation.navigate('CampaignEmailTemplatesScreen', { campaignId: campaigns[0]?.id, category: 'Email' })} />
      </View>

      <SectionHeader title="Recent Campaigns" actionLabel="See all" onAction={() => setActiveTab('Campaigns')} />
      {loading ? (
        <ActivityIndicator color={C.primary} style={{ marginVertical: 20 }} />
      ) : campaigns.length === 0 ? (
        <View style={s.empty}>
          <MaterialCommunityIcons name="bullhorn-outline" size={42} color="#CBD5E1" />
          <Text style={s.emptyText}>{businessId === 0 ? 'Sign in to your business account.' : 'No campaigns yet — create your first one.'}</Text>
        </View>
      ) : (
        campaigns.slice(0, 3).map((c) => (
          <CampaignCard key={String(c.id)} c={c}
            onOpen={(x) => navigation.navigate('CampaignDetails', { campaign: x._raw || x })}
            onAnalytics={(x) => navigation.navigate('CampaignAnalyticsDashboard', { campaign: x._raw || x })}
            onToggle={toggleCampaign} onVideo={goVideo} />
        ))
      )}
      <View style={{ height: 30 }} />
    </ScrollView>
  );

  const renderCampaigns = () => (
    <ScrollView style={s.tabContent} keyboardShouldPersistTaps="handled" refreshControl={<RefreshControl refreshing={refreshing} onRefresh={refresh} tintColor={C.primary} />}>
      <View style={s.rowBetween}>
        <View style={[s.searchWrap, { flex: 1, marginRight: 8 }]}>
          <Ionicons name="search" size={15} color={C.sub} />
          <TextInput style={s.searchInput} value={search} onChangeText={setSearch} placeholder="Search campaigns…" placeholderTextColor="#9CA3AF" />
        </View>
        <TouchableOpacity style={s.newBtn} onPress={() => navigation.navigate('CreateCampaign')}>
          <MaterialCommunityIcons name="plus" size={16} color="#fff" />
          <Text style={s.newBtnText}>New</Text>
        </TouchableOpacity>
      </View>

      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={s.chipRow}>
        <Chip label="All" count={statusCounts.all} active={statusFilter === 'all'} onPress={() => setStatusFilter('all')} />
        {Object.keys(STATUS_META).map((k) => (
          <Chip key={k} label={STATUS_META[k].label} count={statusCounts[k]} active={statusFilter === k} onPress={() => setStatusFilter(k)} />
        ))}
      </ScrollView>

      {loading ? (
        <ActivityIndicator color={C.primary} style={{ marginTop: 30 }} size="large" />
      ) : filteredCampaigns.length === 0 ? (
        <View style={s.empty}>
          <MaterialCommunityIcons name="bullhorn-outline" size={42} color="#CBD5E1" />
          <Text style={s.emptyText}>No campaigns match.</Text>
        </View>
      ) : (
        filteredCampaigns.map((c) => (
          <CampaignCard key={String(c.id)} c={c}
            onOpen={(x) => navigation.navigate('CampaignDetails', { campaign: x._raw || x })}
            onAnalytics={(x) => navigation.navigate('CampaignAnalyticsDashboard', { campaign: x._raw || x })}
            onToggle={toggleCampaign} onVideo={goVideo} />
        ))
      )}
      <TouchableOpacity style={s.linkRow} onPress={() => navigation.navigate('CampaignList')}>
        <Text style={s.linkText}>Open full campaign list</Text>
        <Ionicons name="chevron-forward" size={15} color={C.primary} />
      </TouchableOpacity>
      <View style={{ height: 30 }} />
    </ScrollView>
  );

  const renderContent = () => (
    <ScrollView style={s.tabContent} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={refresh} tintColor={C.primary} />}>
      <SectionHeader title="Content Studio" />
      <View style={s.quickGrid}>
        {STUDIO_TOOLS.map((t) => (
          <QuickAction key={t.key} icon={t.icon} label={t.label} color={t.color}
            onPress={() => {
              if (t.nav === 'CampaignAIContentAssistantScreen') navigation.navigate(t.nav, { campaign: campaigns[0]?._raw });
              else if (t.nav === 'CampaignEmailTemplatesScreen') navigation.navigate(t.nav, { campaignId: campaigns[0]?.id, category: 'Email' });
              else navigation.navigate(t.nav, { campaignId: campaigns[0]?.id, campaign: campaigns[0]?._raw });
            }} />
        ))}
      </View>

      <SectionHeader title={`Content Ideas (${ideas.filter((i) => !i.done).length} open)`} />
      <View style={{ flexDirection: 'row', gap: 8, marginBottom: 10 }}>
        <TouchableOpacity style={[s.aiBtn, { flex: 1 }]} onPress={aiGenerateIdeas} disabled={aiBusy}>
          {aiBusy ? <ActivityIndicator size="small" color={C.accent} /> : <MaterialCommunityIcons name="brain" size={16} color={C.accent} />}
          <Text style={s.aiBtnText}>{aiBusy ? 'Generating…' : 'AI Generate Ideas'}</Text>
        </TouchableOpacity>
        <TouchableOpacity style={[s.newBtn, { paddingHorizontal: 14 }]} onPress={() => setIdeaModal(true)}>
          <MaterialCommunityIcons name="plus" size={16} color="#fff" />
          <Text style={s.newBtnText}>Add</Text>
        </TouchableOpacity>
      </View>

      {ideas.length === 0 ? (
        <View style={s.empty}>
          <MaterialCommunityIcons name="lightbulb-outline" size={42} color="#CBD5E1" />
          <Text style={s.emptyText}>No content ideas yet — generate some with AI.</Text>
        </View>
      ) : (
        ideas.map((i) => <IdeaCard key={i.id} idea={i} onToggle={toggleIdea} onDelete={deleteIdea} />)
      )}
      <View style={{ height: 30 }} />
    </ScrollView>
  );

  const renderAnalytics = () => (
    <ScrollView style={s.tabContent} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={refresh} tintColor={C.primary} />}>
      <SectionHeader title="Performance Overview" />
      <View style={s.statGrid}>
        <StatTile icon="eye-outline" label="Total reach" value={compact(stats.reach)} color={C.purple} />
        <StatTile icon="cursor-default-click-outline" label="Clicks" value={compact(stats.clicks)} color={C.primaryLight} sub={`${stats.ctr}% CTR`} />
        <StatTile icon="target" label="Conversions" value={compact(stats.conv)} color={C.success} sub={`${stats.convRate}% rate`} />
        <StatTile icon="credit-card-outline" label="Spend" value={money(stats.spent)} color={C.warning} sub={stats.conv > 0 ? `${money(stats.spent / stats.conv)} / conv.` : undefined} />
      </View>

      <SectionHeader title="Channel Performance" />
      {channelStats.length === 0 ? (
        <View style={s.empty}><Text style={s.emptyText}>No channel data yet — assign channels to campaigns.</Text></View>
      ) : (
        <View style={s.panel}>
          {channelStats.map((ch) => (
            <BarRow key={ch.key} label={ch.meta.label} value={ch.reach}
              max={channelStats[0]?.reach || 1} color={ch.meta.color} display={compact(Math.round(ch.reach))} />
          ))}
        </View>
      )}

      <SectionHeader title="Top Campaigns by Reach" />
      {topCampaigns.length === 0 ? (
        <View style={s.empty}><Text style={s.emptyText}>No campaign data yet.</Text></View>
      ) : (
        <View style={s.panel}>
          {topCampaigns.map((c, i) => (
            <TouchableOpacity key={String(c.id)} style={s.rankRow} onPress={() => navigation.navigate('CampaignAnalyticsDashboard', { campaign: c._raw || c })}>
              <Text style={s.rankNum}>{i + 1}</Text>
              <View style={{ flex: 1 }}>
                <Text style={s.rankName} numberOfLines={1}>{c.name}</Text>
                <Text style={s.rankMeta}>{compact(c.impressions)} reach · {compact(c.clicks)} clicks · {money(c.spent)}</Text>
              </View>
              <Ionicons name="chevron-forward" size={15} color={C.sub} />
            </TouchableOpacity>
          ))}
        </View>
      )}
      <View style={{ height: 30 }} />
    </ScrollView>
  );

  const renderInsights = () => (
    <ScrollView style={s.tabContent} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={refresh} tintColor={C.primary} />}>
      <View style={s.heroCard}>
        <View style={{ flex: 1 }}>
          <Text style={s.heroTitle}>AI Insights</Text>
          <Text style={s.heroSub}>Analysis based on your live campaign data</Text>
        </View>
        <MaterialCommunityIcons name="brain" size={38} color="rgba(255,255,255,0.35)" />
      </View>

      <TouchableOpacity style={[s.aiBtn, { paddingVertical: 13, marginTop: 4 }]} onPress={generateInsights} disabled={insightsBusy}>
        {insightsBusy ? <ActivityIndicator size="small" color={C.accent} /> : <MaterialCommunityIcons name="auto-fix" size={17} color={C.accent} />}
        <Text style={s.aiBtnText}>{insightsBusy ? 'Analysing your campaigns…' : insights ? 'Regenerate Insights' : 'Generate Insights'}</Text>
      </TouchableOpacity>

      {insights ? (
        <View style={[s.panel, { marginTop: 12 }]}>
          <Text style={s.insightsText}>{insights}</Text>
        </View>
      ) : (
        <View style={[s.panel, { marginTop: 12 }]}>
          <Text style={s.tipTitle}>While you wait — fundamentals that always apply:</Text>
          {[
            'Reply to every comment within 24 hours — engagement compounds.',
            'Video posts outperform static images on every platform.',
            'One clear call to action per post beats three.',
            'Track cost per conversion, not just reach.',
          ].map((t) => (
            <View key={t} style={s.tipRow}>
              <Ionicons name="checkmark-circle" size={15} color={C.success} />
              <Text style={s.tipText}>{t}</Text>
            </View>
          ))}
        </View>
      )}
      <View style={{ height: 30 }} />
    </ScrollView>
  );

  const renderTab = () => {
    switch (activeTab) {
      case 'Dashboard': return renderDashboard();
      case 'Campaigns': return renderCampaigns();
      case 'Content': return renderContent();
      case 'Analytics': return renderAnalytics();
      case 'Insights': return renderInsights();
      default: return renderDashboard();
    }
  };

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------

  return (
    <SafeAreaView style={s.safe} edges={['top']}>
      <View style={s.header}>
        <TouchableOpacity style={s.headerBtn} onPress={() => navigation.goBack()}>
          <Ionicons name="chevron-back" size={24} color="#fff" />
        </TouchableOpacity>
        <View style={{ flex: 1 }}>
          <Text style={s.headerTitle}>Marketing & Promotion</Text>
          <Text style={s.headerSub}>{companyName || ' '}</Text>
        </View>
        <DbBadge status={dbStatus} onPress={showDbDetail} />
        <View style={{ marginLeft: 8 }}>
          <CompanyLogo uri={companyLogo} size={30} />
        </View>
      </View>

      <View style={s.tabBar}>
        {TABS.map((t) => (
          <TouchableOpacity key={t.key} style={[s.tabBtn, activeTab === t.key && s.tabBtnActive]} onPress={() => setActiveTab(t.key)}>
            <MaterialCommunityIcons name={t.icon} size={17} color={activeTab === t.key ? C.primary : 'rgba(255,255,255,0.75)'} />
            <Text style={[s.tabText, activeTab === t.key && s.tabTextActive]}>{t.key}</Text>
          </TouchableOpacity>
        ))}
      </View>

      {renderTab()}

      {/* Add Idea Modal */}
      <Modal visible={ideaModal} transparent animationType="fade" onRequestClose={() => setIdeaModal(false)}>
        <View style={s.modalOverlay}>
          <View style={s.modalBox}>
            <Text style={s.modalTitle}>New content idea</Text>
            <TextInput style={[s.input, { marginTop: 12 }]} value={ideaTitle} onChangeText={setIdeaTitle}
              placeholder="e.g. Behind the scenes on our latest project" placeholderTextColor="#9CA3AF" />
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
              {IDEA_TYPES.map((t) => (
                <Chip key={t} label={t} active={ideaType === t} onPress={() => setIdeaType(t)} />
              ))}
            </View>
            <View style={s.modalBtnRow}>
              <TouchableOpacity style={s.modalCancel} onPress={() => setIdeaModal(false)}>
                <Text style={s.modalCancelText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={s.modalOk} onPress={addIdea}>
                <Text style={s.modalOkText}>Add Idea</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
};

// =============================================================================
// STYLES
// =============================================================================

const s = StyleSheet.create({
  safe: { flex: 1, backgroundColor: C.primary },
  header: { flexDirection: 'row', alignItems: 'center', backgroundColor: C.primary, paddingHorizontal: 10, paddingVertical: 10 },
  headerBtn: { padding: 6 },
  headerTitle: { color: '#fff', fontSize: 17, fontWeight: '800' },
  headerSub: { color: 'rgba(255,255,255,0.65)', fontSize: 11 },

  dbBadge: { flexDirection: 'row', alignItems: 'center', gap: 5, backgroundColor: 'rgba(255,255,255,0.14)', borderRadius: 12, paddingHorizontal: 9, paddingVertical: 5 },
  dbDot: { width: 7, height: 7, borderRadius: 4 },
  dbText: { color: '#fff', fontSize: 10.5, fontWeight: '700' },

  tabBar: { flexDirection: 'row', backgroundColor: C.primary, paddingHorizontal: 6, paddingBottom: 8, gap: 4 },
  tabBtn: { flex: 1, alignItems: 'center', paddingVertical: 7, borderRadius: 12, backgroundColor: 'rgba(255,255,255,0.1)' },
  tabBtnActive: { backgroundColor: '#fff' },
  tabText: { fontSize: 9.5, fontWeight: '700', color: 'rgba(255,255,255,0.75)', marginTop: 2 },
  tabTextActive: { color: C.primary },

  tabContent: { flex: 1, backgroundColor: C.bg, paddingHorizontal: 14, paddingTop: 12 },

  heroCard: { flexDirection: 'row', alignItems: 'center', backgroundColor: C.primaryDark, borderRadius: 16, padding: 16, marginBottom: 12 },
  heroTitle: { color: '#fff', fontSize: 18, fontWeight: '800' },
  heroSub: { color: 'rgba(255,255,255,0.7)', fontSize: 12, marginTop: 3 },

  statGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  statTile: { width: (SCREEN_W - 36) / 2, backgroundColor: C.card, borderRadius: 14, padding: 12, borderWidth: 1, borderColor: C.border },
  statIcon: { width: 30, height: 30, borderRadius: 9, alignItems: 'center', justifyContent: 'center', marginBottom: 7 },
  statValue: { fontSize: 17, fontWeight: '800', color: C.text },
  statLabel: { fontSize: 11, color: C.sub, marginTop: 1 },
  statSub: { fontSize: 10, fontWeight: '700', marginTop: 2 },

  sectionHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 16, marginBottom: 8 },
  sectionTitle: { fontSize: 14.5, fontWeight: '800', color: C.text },
  sectionAction: { fontSize: 12, fontWeight: '700', color: C.primary },

  quickGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  quickAction: { width: (SCREEN_W - 44) / 3, backgroundColor: C.card, borderRadius: 14, padding: 12, alignItems: 'center', borderWidth: 1, borderColor: C.border },
  quickIcon: { width: 40, height: 40, borderRadius: 12, alignItems: 'center', justifyContent: 'center', marginBottom: 6 },
  quickLabel: { fontSize: 11, fontWeight: '700', color: C.text, textAlign: 'center' },

  rowBetween: { flexDirection: 'row', alignItems: 'center' },
  searchWrap: { flexDirection: 'row', alignItems: 'center', backgroundColor: C.card, borderRadius: 10, paddingHorizontal: 10, borderWidth: 1, borderColor: C.border },
  searchInput: { flex: 1, paddingVertical: Platform.OS === 'ios' ? 9 : 7, paddingHorizontal: 8, fontSize: 13.5, color: C.text },
  newBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, backgroundColor: C.primary, paddingHorizontal: 12, paddingVertical: 9, borderRadius: 10 },
  newBtnText: { color: '#fff', fontSize: 12.5, fontWeight: '700' },

  chipRow: { gap: 8, paddingVertical: 10 },
  chip: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 15, backgroundColor: C.card, borderWidth: 1, borderColor: C.border },
  chipActive: { backgroundColor: C.primary, borderColor: C.primary },
  chipText: { fontSize: 12, fontWeight: '600', color: C.sub },
  chipTextActive: { color: '#fff' },

  campCard: { backgroundColor: C.card, borderRadius: 14, padding: 14, marginBottom: 10, borderWidth: 1, borderColor: C.border },
  campHead: { flexDirection: 'row', alignItems: 'flex-start' },
  campName: { fontSize: 14.5, fontWeight: '700', color: C.text },
  campObjective: { fontSize: 11.5, color: C.primaryLight, fontWeight: '600', marginTop: 2 },
  statusPill: { paddingHorizontal: 9, paddingVertical: 4, borderRadius: 10 },
  statusPillText: { fontSize: 10.5, fontWeight: '800' },

  channelRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 8 },
  channelBadge: { flexDirection: 'row', alignItems: 'center', gap: 4, borderRadius: 7, paddingHorizontal: 7, paddingVertical: 3 },
  channelText: { fontSize: 10, fontWeight: '700' },

  budgetRow: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 10 },
  budgetText: { fontSize: 12, fontWeight: '800', color: C.text },
  budgetPct: { fontSize: 11.5, fontWeight: '700' },
  track: { height: 5, backgroundColor: '#E8EDF6', borderRadius: 3, marginTop: 5, overflow: 'hidden' },
  fill: { height: 5, borderRadius: 3 },

  metricsRow: { flexDirection: 'row', marginTop: 10, backgroundColor: '#F8FAFD', borderRadius: 10, paddingVertical: 8 },
  metric: { flex: 1, alignItems: 'center' },
  metricV: { fontSize: 12.5, fontWeight: '800', color: C.text },
  metricL: { fontSize: 9.5, color: C.sub, marginTop: 1 },

  campActions: { flexDirection: 'row', gap: 6, marginTop: 10, borderTopWidth: 1, borderTopColor: C.border, paddingTop: 9 },
  campBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 9, paddingVertical: 6, borderRadius: 8, backgroundColor: '#F8FAFD' },
  campBtnText: { fontSize: 11.5, fontWeight: '700', color: C.primary },

  linkRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 4, paddingVertical: 12 },
  linkText: { fontSize: 12.5, fontWeight: '700', color: C.primary },

  empty: { alignItems: 'center', paddingVertical: 26, paddingHorizontal: 24 },
  emptyText: { fontSize: 12.5, color: C.sub, textAlign: 'center', marginTop: 8, lineHeight: 18 },

  aiBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, backgroundColor: '#FCE7F3', borderRadius: 10, paddingVertical: 10 },
  aiBtnText: { fontSize: 12.5, fontWeight: '800', color: C.accent },

  ideaCard: { flexDirection: 'row', alignItems: 'center', backgroundColor: C.card, borderRadius: 12, padding: 11, marginBottom: 8, borderWidth: 1, borderColor: C.border },
  ideaCheck: { marginRight: 10 },
  ideaTitle: { fontSize: 13, fontWeight: '600', color: C.text, lineHeight: 18 },
  ideaMetaRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 4 },
  ideaType: { backgroundColor: '#EFF4FF', borderRadius: 6, paddingHorizontal: 7, paddingVertical: 2 },
  ideaTypeText: { fontSize: 9.5, fontWeight: '700', color: C.primary },
  ideaSource: { fontSize: 9.5, color: C.sub, fontWeight: '600' },

  panel: { backgroundColor: C.card, borderRadius: 14, padding: 14, borderWidth: 1, borderColor: C.border },

  barRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 6, gap: 8 },
  barLabel: { width: 76, fontSize: 11.5, fontWeight: '700', color: C.text },
  barTrack: { flex: 1, height: 8, backgroundColor: '#EEF2F8', borderRadius: 4, overflow: 'hidden' },
  barFill: { height: 8, borderRadius: 4 },
  barValue: { width: 48, fontSize: 11, fontWeight: '700', color: C.sub, textAlign: 'right' },

  rankRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 9, gap: 10 },
  rankNum: { width: 22, height: 22, borderRadius: 11, backgroundColor: C.primary, color: '#fff', fontSize: 11, fontWeight: '800', textAlign: 'center', lineHeight: 22, overflow: 'hidden' },
  rankName: { fontSize: 13, fontWeight: '700', color: C.text },
  rankMeta: { fontSize: 10.5, color: C.sub, marginTop: 1 },

  insightsText: { fontSize: 13.5, lineHeight: 21, color: C.text },
  tipTitle: { fontSize: 12.5, fontWeight: '800', color: C.text, marginBottom: 8 },
  tipRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 7, paddingVertical: 4 },
  tipText: { flex: 1, fontSize: 12.5, color: C.sub, lineHeight: 18 },

  input: { backgroundColor: '#F8FAFD', borderWidth: 1, borderColor: C.border, borderRadius: 10, paddingHorizontal: 12, paddingVertical: Platform.OS === 'ios' ? 10 : 8, fontSize: 13.5, color: C.text },

  modalOverlay: { flex: 1, backgroundColor: 'rgba(15,23,42,0.5)', justifyContent: 'center', padding: 22 },
  modalBox: { backgroundColor: C.card, borderRadius: 16, padding: 18 },
  modalTitle: { fontSize: 16, fontWeight: '800', color: C.text },
  modalBtnRow: { flexDirection: 'row', gap: 10, marginTop: 16 },
  modalCancel: { flex: 1, alignItems: 'center', paddingVertical: 11, borderRadius: 10, backgroundColor: '#F1F5F9' },
  modalCancelText: { fontSize: 13.5, fontWeight: '700', color: C.sub },
  modalOk: { flex: 1, alignItems: 'center', paddingVertical: 11, borderRadius: 10, backgroundColor: C.primary },
  modalOkText: { fontSize: 13.5, fontWeight: '800', color: '#fff' },
});

export default MarketingPromotionScreen;
